import React from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import toast from "react-hot-toast";

import { Benefit, BenefitImage } from "../../models/benefits";
import { BENEFIT_MAX_IMAGES } from "../../utils/constants/benefits-constants";
import {
  useDeleteBenefitImageMutation,
  useReorderBenefitImagesMutation,
} from "../../store/services/benefits";
import { AppDialog } from "../ui";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";

const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

const sortSavedImages = (images?: BenefitImage[]): BenefitImage[] =>
  [...(images ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

const tileStyle: React.CSSProperties = {
  width: 120,
  height: 120,
  borderRadius: 8,
  overflow: "hidden",
  position: "relative",
  cursor: "grab",
};

type DragSource = { kind: "saved" | "pending"; index: number };

interface BenefitImagesManagerProps {
  /** Presente solo en modo edición: habilita reorder/delete de imágenes guardadas. */
  benefit?: Benefit;
  /** Archivos nuevos pendientes de subir (se agregan al final al guardar). */
  pendingFiles: File[];
  onPendingFilesChange: (files: File[]) => void;
  disabled?: boolean;
}

export const BenefitImagesManager = ({
  benefit,
  pendingFiles,
  onPendingFilesChange,
  disabled = false,
}: BenefitImagesManagerProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dragSourceRef = React.useRef<DragSource | null>(null);

  // Orden local optimista de las imágenes guardadas (se re-sincroniza con el server).
  const [savedImages, setSavedImages] = React.useState<BenefitImage[]>(() =>
    sortSavedImages(benefit?.images)
  );

  React.useEffect(() => {
    setSavedImages(sortSavedImages(benefit?.images));
  }, [benefit?.images]);

  const [imageToDelete, setImageToDelete] = React.useState<
    BenefitImage | undefined
  >();

  const [reorderBenefitImages, { isLoading: isReordering }] =
    useReorderBenefitImagesMutation();
  const [deleteBenefitImage, { isLoading: isDeletingImage }] =
    useDeleteBenefitImageMutation();

  // Previews de los archivos pendientes (se revocan al cambiar la lista / desmontar).
  const pendingPreviews = React.useMemo(
    () => pendingFiles.map((file) => URL.createObjectURL(file)),
    [pendingFiles]
  );

  React.useEffect(
    () => () => {
      pendingPreviews.forEach((url) => URL.revokeObjectURL(url));
    },
    [pendingPreviews]
  );

  const totalImages = savedImages.length + pendingFiles.length;

  const handleAddFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (selectedFiles.length === 0) return;

    const validFiles: File[] = [];
    selectedFiles.forEach((file) => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`"${file.name}": formato no permitido (PNG, JPG o WebP)`);
        return;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        toast.error(`"${file.name}": supera el máximo de 5 MB`);
        return;
      }
      validFiles.push(file);
    });

    if (validFiles.length === 0) return;

    const availableSlots = BENEFIT_MAX_IMAGES - totalImages;
    if (validFiles.length > availableSlots) {
      toast.error(`Máximo ${BENEFIT_MAX_IMAGES} imágenes`);
    }
    const filesToAdd = validFiles.slice(0, Math.max(availableSlots, 0));
    if (filesToAdd.length > 0) {
      onPendingFilesChange([...pendingFiles, ...filesToAdd]);
    }
  };

  const handleSavedDrop = (targetIndex: number) => {
    const source = dragSourceRef.current;
    dragSourceRef.current = null;
    if (!benefit || !source || source.kind !== "saved") return;
    if (source.index === targetIndex) return;

    const previousOrder = savedImages;
    const nextOrder = [...savedImages];
    const [moved] = nextOrder.splice(source.index, 1);
    nextOrder.splice(targetIndex, 0, moved);
    setSavedImages(nextOrder);

    // El server exige el set EXACTO de ids guardados en el nuevo orden.
    reorderBenefitImages({
      benefitId: benefit.id,
      imageIds: nextOrder.map((image) => image.id),
    })
      .unwrap()
      .catch((err) => {
        setSavedImages(previousOrder);
        toast.error(
          getAdminFacingApiErrorMessage(
            err,
            "No se pudo reordenar las imágenes"
          )
        );
      });
  };

  const handlePendingDrop = (targetIndex: number) => {
    const source = dragSourceRef.current;
    dragSourceRef.current = null;
    if (!source || source.kind !== "pending") return;
    if (source.index === targetIndex) return;

    const nextOrder = [...pendingFiles];
    const [moved] = nextOrder.splice(source.index, 1);
    nextOrder.splice(targetIndex, 0, moved);
    onPendingFilesChange(nextOrder);
  };

  const handleDeleteSavedImage = () => {
    if (!benefit || !imageToDelete) return;
    deleteBenefitImage({ benefitId: benefit.id, imageId: imageToDelete.id })
      .unwrap()
      .then(() => {
        setSavedImages((current) =>
          current.filter((image) => image.id !== imageToDelete.id)
        );
        toast.success("Imagen eliminada");
      })
      .catch((err) => {
        toast.error(
          getAdminFacingApiErrorMessage(err, "No se pudo eliminar la imagen")
        );
      })
      .finally(() => setImageToDelete(undefined));
  };

  const removePendingFile = (index: number) => {
    onPendingFilesChange(pendingFiles.filter((_, i) => i !== index));
  };

  const coverTag = (
    <Tag
      value="Portada"
      severity="info"
      style={{ position: "absolute", top: 4, left: 4, zIndex: 1 }}
    />
  );

  const deleteTileButton = (onClick: () => void, ariaLabel: string) => (
    <Button
      type="button"
      icon="pi pi-trash"
      rounded
      severity="danger"
      size="small"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled || isDeletingImage || isReordering}
      style={{
        position: "absolute",
        top: 4,
        right: 4,
        width: 28,
        height: 28,
        padding: 0,
      }}
    />
  );

  return (
    <div className="flex flex-column gap-3">
      {/* Aclaración arriba, como intro de la sección (no texto suelto debajo). */}
      <p className="mt-0 mb-0 text-600 line-height-3">
        La primera imagen es la portada. Arrastrá las miniaturas para
        reordenarlas.
        {benefit ? " Las imágenes nuevas se agregan al final al guardar." : ""}
      </p>
      <div className="flex flex-wrap gap-3">
        {savedImages.map((image, index) => (
          <div
            key={image.id}
            style={tileStyle}
            className="surface-200"
            draggable={!disabled}
            onDragStart={() => {
              dragSourceRef.current = { kind: "saved", index };
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleSavedDrop(index);
            }}
          >
            {index === 0 && coverTag}
            <img
              src={image.imageUrl ?? undefined}
              alt={`Imagen ${index + 1}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              draggable={false}
            />
            {deleteTileButton(
              () => setImageToDelete(image),
              "Eliminar imagen guardada"
            )}
          </div>
        ))}

        {pendingFiles.map((file, index) => (
          <div
            key={`${file.name}-${file.size}-${index}`}
            style={{ ...tileStyle, border: "2px dashed var(--surface-400)" }}
            className="surface-100"
            draggable={!disabled}
            onDragStart={() => {
              dragSourceRef.current = { kind: "pending", index };
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handlePendingDrop(index);
            }}
          >
            {savedImages.length === 0 && index === 0 && coverTag}
            <img
              src={pendingPreviews[index]}
              alt={file.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              draggable={false}
            />
            <Tag
              value="Nueva"
              severity="warning"
              style={{ position: "absolute", bottom: 4, left: 4, zIndex: 1 }}
            />
            {deleteTileButton(
              () => removePendingFile(index),
              "Quitar imagen pendiente"
            )}
          </div>
        ))}

        {totalImages === 0 && (
          <div
            className="flex align-items-center justify-content-center surface-100 text-color-secondary"
            style={{ ...tileStyle, cursor: "default", border: "2px dashed var(--surface-400)" }}
          >
            <i className="pi pi-image text-3xl" />
          </div>
        )}
      </div>

      <div className="flex align-items-center gap-3">
        <Button
          type="button"
          label="Agregar imágenes"
          icon="pi pi-plus"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || totalImages >= BENEFIT_MAX_IMAGES}
        />
        <small className="text-color-secondary">
          {totalImages}/{BENEFIT_MAX_IMAGES} imágenes · PNG, JPG o WebP · máx. 5
          MB cada una
        </small>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(",")}
        multiple
        hidden
        onChange={handleAddFiles}
      />

      <AppDialog
        variant="destructive"
        visible={!!imageToDelete}
        onHide={() => setImageToDelete(undefined)}
        header="Eliminar imagen"
        primaryLabel="Eliminar Imagen"
        onPrimary={handleDeleteSavedImage}
        loading={isDeletingImage}
      >
        <span>
          ¿Estás seguro de que deseás eliminar esta imagen?
          <br />
          <small className="text-color-secondary">
            Esta acción no se puede deshacer.
          </small>
        </span>
      </AppDialog>
    </div>
  );
};
